/**
 * PLP price/label reconciliation (initial load + lazy load).
 * Keeps the regular price visible and hides only the discounted price + dynamic discount labels
 * (tier / Regios) per card until that card is reconciled, then reveals it immediately. Computes
 * discounts directly via the Regios API so reveal isn't gated on a fixed delay or DOM rescan.
 */
(function () {
  'use strict';

  // Safety: max time the discounted price/labels stay hidden before we reveal regardless.
  var REVEAL_SAFETY_TIMEOUT_MS = 5000;

  // Cards whose discounts are driven by this single-pass script. On these cards the tier / Regios
  // badge web components defer to us instead of each running their own calculateDiscountedPrices.
  var managedCards = new WeakSet();

  function markManagedCards(cards) {
    if (!cards) return;
    Array.prototype.forEach.call(cards, (card) => {
      if (card) managedCards.add(card);
    });
  }

  function isManagedDiscountCard(el) {
    if (!el || !el.closest) return false;
    const card = el.closest('product-card');
    return !!card && managedCards.has(card);
  }

  function waitRegiosDOPP() {
    return new Promise((resolve) => {
      if (window.RegiosDOPP?.api?.v0) {
        resolve();
      } else {
        window.addEventListener('regios-dopp:api-initialized', () => resolve(), { once: true });
      }
    });
  }

  function buildApiParams(card, collectionId) {
    const productId = parseInt(card.dataset.regiosDoppGenericProductId, 10);
    const variantId = parseInt(card.dataset.jklVariantId, 10);
    const regularPriceInCents = parseInt(card.dataset.jklRegularPrice, 10);

    if (!productId || !variantId || !regularPriceInCents) return null;

    const params = { productId, variantId, regularPriceInCents };

    const compareAtPriceInCents = parseInt(card.dataset.jklCompareAtPrice, 10);
    if (compareAtPriceInCents) params.compareAtPriceInCents = compareAtPriceInCents;

    const parsedCollectionId = parseInt(collectionId, 10);
    if (parsedCollectionId) params.collectionIds = [parsedCollectionId];

    if (card.dataset.jklHandle) params.handle = card.dataset.jklHandle;
    if (card.dataset.jklVendor) params.vendor = card.dataset.jklVendor;
    if (card.dataset.jklUrl) params.url = card.dataset.jklUrl;

    if (card.dataset.jklTags) {
      params.tags = card.dataset.jklTags.split(',').map((t) => t.trim()).filter(Boolean);
    }

    return params;
  }

  function formatMoney(cents) {
    const settings = window.themeVariables?.settings ?? {};
    const format = settings.currencyCodeEnabled && settings.moneyWithCurrencyFormat
      ? settings.moneyWithCurrencyFormat
      : (settings.moneyFormat || '{{amount}}');

    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, format);
    }
    return (cents / 100).toFixed(2);
  }

  /** Regios amount is usually cents; normalize when API returns major units (e.g. 14.99 vs 1499). */
  function normalizeCents(amount, referenceCents) {
    if (amount == null) return null;
    const n = Number(amount);
    if (!Number.isFinite(n)) return null;
    if (referenceCents >= 100 && n > 0 && n < referenceCents / 10) {
      return Math.round(n * 100);
    }
    return Math.round(n);
  }

  function extractFormattedText(formatted) {
    if (!formatted) return '';
    const value = String(formatted);
    if (!/<[a-z][\s\S]*>/i.test(value)) return value.trim();
    const template = document.createElement('template');
    template.innerHTML = value.trim();
    return template.content.textContent.trim();
  }

  function applyCurrencyFromReference(amountText, referenceText) {
    if (!referenceText || /[€$£¥]/.test(amountText)) return amountText;

    const leadingSymbol = referenceText.match(/^([^\d\s,.-]+)/)?.[1]?.trim();
    if (leadingSymbol) return leadingSymbol + amountText;

    const trailingSymbol = referenceText.match(/([^\d\s,.-]+)$/)?.[1]?.trim();
    if (trailingSymbol) return `${amountText} ${trailingSymbol}`;

    return amountText;
  }

  function getCardMoneyReference(card) {
    const moneyEl = card.querySelector(
      '.regios-dopp-generic-price-item--sale .money, .regios-dopp-generic-price-item--regular .money',
    );
    return moneyEl?.textContent?.trim() || '';
  }

  function formatCentsAsMoneyHtml(cents, referenceFormatted) {
    const referenceText = extractFormattedText(referenceFormatted) || referenceFormatted || '';
    const amount = applyCurrencyFromReference(formatMoney(cents), referenceText);
    return `<span class="money">${amount}</span>`;
  }

  function priceToFormatted(priceObj, fallbackCents, referenceFormatted) {
    if (priceObj?.formatted) return priceObj.formatted;
    const cents = normalizeCents(priceObj?.amount, fallbackCents) ?? fallbackCents;
    return formatCentsAsMoneyHtml(cents, referenceFormatted);
  }

  function setPriceEl(el, formatted) {
    const sr = el.querySelector('.sr-only');
    el.replaceChildren();
    if (sr) el.appendChild(sr);

    const value = String(formatted ?? '');
    if (/<[a-z][\s\S]*>/i.test(value)) {
      const template = document.createElement('template');
      template.innerHTML = value.trim();
      el.append(...template.content.childNodes);
      return;
    }

    const moneySpan = document.createElement('span');
    moneySpan.className = 'money';
    moneySpan.textContent = value;
    el.appendChild(moneySpan);
  }

  function applySaleMarkup(card, saleFormatted, regularFormatted) {
    const container = card.querySelector('.regios-dopp-generic-price-container');
    const saleEl = card.querySelector('.regios-dopp-generic-price-item--sale');
    if (!saleEl) return;

    setPriceEl(saleEl, saleFormatted);
    saleEl.classList.add('text-on-sale');

    let regularEl = card.querySelector('.regios-dopp-generic-price-item--regular');
    if (!regularEl) {
      regularEl = document.createElement('compare-at-price');
      regularEl.className = 'text-subdued line-through regios-dopp-generic-price-item--regular';
      saleEl.insertAdjacentElement('afterend', regularEl);
    }

    regularEl.classList.remove('hidden');
    regularEl.removeAttribute('aria-hidden');
    regularEl.style.textDecoration = 'line-through';
    regularEl.setAttribute('data-made-visible-by-regios-dopp', 'true');
    setPriceEl(regularEl, regularFormatted);

    container?.classList.add('regios-dopp-generic-price--on-sale');
  }

  function hasNativeSale(card) {
    const regularEl = card.querySelector('.regios-dopp-generic-price-item--regular');
    return regularEl && !regularEl.classList.contains('hidden');
  }

  /** Replicates jkl-tier-discount-badge: hide the tier badge when Regios is as good or better. */
  function updateTierBadge(card, regularCents, apiSaleCents, tierPercent) {
    const tierBadge = card.querySelector('jkl-tier-discount-badge');
    if (!tierBadge || !(tierPercent > 0)) return;
    if (apiSaleCents == null || apiSaleCents >= regularCents) return;

    const tierSaleCents = Math.round(regularCents * (1 - tierPercent / 100));
    if (apiSaleCents <= tierSaleCents) tierBadge.style.display = 'none';
  }

  /** Replicates jkl-regios-on-sale-badge: reveal the Regios sale badge when its promo applies. */
  function updateRegiosBadge(card, regularCents, apiSaleCents, tierPercent) {
    const badge = card.querySelector('jkl-regios-on-sale-badge');
    if (!badge || badge.dataset.hasNativeDiscount === 'true') return;
    // Exclusive promo products keep the discounted price (handled above) but must not show the
    // Regios label, to avoid duplicating the exclusive promo badge.
    if (badge.dataset.suppressBadge === 'true') return;
    if (apiSaleCents == null || apiSaleCents >= regularCents) return;

    if (tierPercent > 0) {
      const tierSaleCents = Math.round(regularCents * (1 - tierPercent / 100));
      if (apiSaleCents > tierSaleCents) return;
    }

    let badgeText;
    if (badge.dataset.discountMode === 'percentage') {
      badgeText = `- ${Math.round(((regularCents - apiSaleCents) / regularCents) * 100)}%`;
    } else {
      const saved = regularCents - apiSaleCents;
      const moneyFormat = badge.dataset.moneyFormat || '{{amount}}';
      const savedText = typeof Shopify !== 'undefined' && Shopify.formatMoney
        ? Shopify.formatMoney(saved, moneyFormat)
        : (saved / 100).toFixed(2);
      badgeText = `- ${savedText}`;
    }

    badge.innerHTML = `<on-sale-badge class="badge badge--on-sale">${badgeText}</on-sale-badge>`;
    badge.style.display = '';
  }

  /**
   * Single reconciliation pass for one card: ONE calculateDiscountedPrices call drives the price,
   * the tier badge and the Regios sale badge together. Replaces the three independent calls that
   * the price script and the two badge web components used to make per card (those defer to us on
   * managed PLP cards), removing duplicate work and the race between them.
   */
  async function reconcileCard(card, collectionId, moneyReference) {
    const container = card.querySelector('.regios-dopp-generic-price-container');
    if (!container) return;

    // Native Shopify sales are fully handled server-side; nothing dynamic to apply.
    if (hasNativeSale(card)) return;
    if (container.classList.contains('regios-dopp-generic-price--on-sale')) return;

    const params = buildApiParams(card, collectionId);
    if (!params) return;

    const { salePrice, regularPrice } = await window.RegiosDOPP.api.v0.calculateDiscountedPrices(params);
    const regularCents = params.regularPriceInCents;
    const apiSaleCents = salePrice?.amount != null ? normalizeCents(salePrice.amount, regularCents) : null;

    const tierBadge = card.querySelector('jkl-tier-discount-badge');
    const tierPercent = tierBadge ? parseFloat(tierBadge.dataset.tierPercent) || 0 : 0;
    const hasTier = tierPercent > 0;

    const liquidMoneyReference = moneyReference || getCardMoneyReference(card);
    const regularFormatted = priceToFormatted(regularPrice, regularCents, liquidMoneyReference);
    const currencyReference = extractFormattedText(regularFormatted) || liquidMoneyReference;

    if (hasTier) {
      const tierSaleCents = Math.round(regularCents * (1 - tierPercent / 100));
      const useRegios = apiSaleCents != null && apiSaleCents < regularCents && apiSaleCents <= tierSaleCents;
      const finalSaleCents = useRegios ? apiSaleCents : tierSaleCents;

      if (finalSaleCents < regularCents) {
        const saleFormatted = useRegios
          ? priceToFormatted(salePrice, regularCents, currencyReference)
          : formatCentsAsMoneyHtml(tierSaleCents, currencyReference);
        applySaleMarkup(card, saleFormatted, regularFormatted);
      }
    } else if (apiSaleCents != null && apiSaleCents < regularCents) {
      applySaleMarkup(
        card,
        priceToFormatted(salePrice, regularCents, currencyReference),
        regularFormatted,
      );
    }

    updateTierBadge(card, regularCents, apiSaleCents, tierPercent);
    updateRegiosBadge(card, regularCents, apiSaleCents, tierPercent);
  }

  /**
   * Reconcile a set of product cards. The regular price stays visible the whole time; only the
   * discounted price + dynamic labels (tier / Regios) are hidden per card and revealed as soon as
   * THAT card is reconciled (not after the whole batch), for the fastest perceived load. A per-card
   * safety timer guarantees a card is never left stuck hidden.
   */
  async function notifyRegiosLazyLoad(productCards, options = {}) {
    if (!productCards.length) return;

    markManagedCards(productCards);

    const safetyTimers = new Map();
    productCards.forEach((card) => {
      card.classList.add('jkl-discounts-pending');
      safetyTimers.set(
        card,
        setTimeout(() => card.classList.remove('jkl-discounts-pending'), REVEAL_SAFETY_TIMEOUT_MS),
      );
    });

    const reveal = (card) => {
      clearTimeout(safetyTimers.get(card));
      card.classList.remove('jkl-discounts-pending');
    };

    await waitRegiosDOPP();

    // Let Regios know about the cards (required for lazy-loaded nodes it does not auto-scan).
    // Skipped on initial load: Regios already auto-scans the page there, so dispatching would
    // trigger a redundant full rescan competing with our per-card reconciliation below.
    // Fire-and-forget: our own reconciliation drives the reveal regardless.
    if (!options.skipNewItemsEvent) {
      window.dispatchEvent(
        new CustomEvent('regios-dopp:collection-page:new-items', {
          detail: { uniqueKey: options.uniqueKey || 'default', newItems: [] },
        }),
      );
    }

    await Promise.all(
      productCards.map((card) =>
        reconcileCard(card, options.collectionId, getCardMoneyReference(card))
          .catch(() => {})
          .finally(() => reveal(card)),
      ),
    );
  }

  function processInitialList(list) {
    var cards = Array.from(list.querySelectorAll(':scope > product-card'));

    var done = notifyRegiosLazyLoad(cards, {
      uniqueKey: list.dataset.jklRegiosUniqueKey || 'default',
      collectionId: list.dataset.jklCollectionId,
      skipNewItemsEvent: true,
    });

    // notifyRegiosLazyLoad adds the per-card hide synchronously (before its first await), so the
    // first-paint list-level hide can be dropped now without exposing any unreconciled discount.
    // Each card then reveals individually as soon as it is reconciled.
    list.classList.remove('jkl-discounts-pending');

    return done;
  }

  function initCollectionPrices() {
    var lists = Array.from(document.querySelectorAll('product-list.jkl-discounts-pending'));

    lists.forEach((list) => {
      // Safety: ensure the first-paint hide is dropped even if reconciliation cannot run at all.
      var safety = setTimeout(() => list.classList.remove('jkl-discounts-pending'), REVEAL_SAFETY_TIMEOUT_MS);

      try {
        processInitialList(list)
          .catch(() => {})
          .finally(() => clearTimeout(safety));
      } catch (e) {
        clearTimeout(safety);
        list.classList.remove('jkl-discounts-pending');
      }
    });
  }

  window.JKL = window.JKL || {};
  window.JKL.notifyRegiosLazyLoad = notifyRegiosLazyLoad;
  window.JKL.markManagedCards = markManagedCards;
  window.JKL.isManagedDiscountCard = isManagedDiscountCard;

  // Mark the initial cards synchronously, before the badge components upgrade, so they defer to us.
  // This script runs ahead of the per-card badge scripts in document order, so the marks are in
  // place by the time those custom elements are defined and their connectedCallback runs.
  markManagedCards(document.querySelectorAll('product-list.jkl-discounts-pending > product-card'));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollectionPrices);
  } else {
    initCollectionPrices();
  }
})();
